import { mapState } from 'vuex';
import NiotaClassicApiService from '@/services/niotaClassicApiService';
import formMixin from '../../formMixin';
import parserTypesMixin from './typesMixin';
import get from 'lodash/get';
import set from 'lodash/set';
import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
export default {
  name: 'NiotaClassicDevicetypesPacketParserWrapper',
  mixins: [formMixin, parserTypesMixin],
  props: {
    deviceTypeId: {
      type: String,
      required: false,
      default: null
    },
    deviceTypeData: {
      type: Object,
      required: true
    },
    emitInsteadOfSave: {
      type: Boolean,
      required: false,
      default: false
    },
    hideSaveBtn: {
      type: Boolean,
      required: false,
      default: false
    },
    customTestFunction: {
      type: Function,
      requirded: false,
      default: null
    },
    hidePredefinedParserSelection: {
      type: Boolean,
      required: false,
      default: false
    },
    callFromDeviceType: {
      type: Boolean,
      required: false,
      default: false
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data: function () {
    return {
      originalParserConfig: null,
      formIsDirty: false,
      parserSavePrompt: false,
      parserDuplicateKeysPrompt: false,
      // persist of the `next` function from `beforeRouteLeave`
      unsavePromptNextPersist: null,
      formComponent: null,
      switchParserPrompt: false,
      deviceTypeDataInternal: cloneDeep(this.deviceTypeData)
    };
  },
  computed: {
    deviceTypeParser() {
      // in case parser is null, a `, {})` in the get would not work
      return get(this.deviceTypeDataInternal, 'parser') || {};
    },
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'permissions']),
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    formCardButtons() {
      if (get(this.deviceTypeDataInternal, 'parser.type', 'unset') === 'unset') {
        return [];
      }
      const btns = [{
        icon: 'fal fa-repeat-alt',
        color: 'danger',
        title: this.$i18n_t('NiotaClassic.DeviceTypes.form.parser.switchParserButton'),
        tooltip: this.$i18n_t('NiotaClassic.DeviceTypes.form.parser.switchParserButton'),
        action: 'switch-parser-type',
        disabled: this.readonly,
        valid: this.isIotHubManager && get(this.deviceTypeDataInternal, 'parser.type', 'unset') !== 'unset'
      }];
      if (this.hideSaveBtn !== true) {
        btns.push({
          icon: 'fas fa-save',
          title: this.$i18n_t('Common.save'),
          tooltip: this.$i18n_t('Common.save'),
          action: 'submit-form',
          disabled: this.readonly,
          valid: this.isIotHubManager ? undefined : false,
          type: 'submit'
        });
      }
      return btns;
    },
    parserTypeName() {
      const type = get(this.deviceTypeDataInternal, 'parser.type', 'unset');
      return this.$i18n_t('NiotaClassic.DeviceTypes.form.parser.parserTypes')[type];
    }
  },
  watch: {
    deviceTypeData: {
      handler(newVal) {
        this.deviceTypeDataInternal = cloneDeep(newVal);
      },
      deep: true
    },
    deviceTypeDataInternal() {
      if (this.deviceTypeData && !this.deviceTypeData.parser) {
        this.deviceTypeData.parser = {};
      }
      this.$nextTick(() => {
        this.formComponent = get(this.$refs, 'formComponent.formElement', null);
      });
    },
    deviceTypeParser: {
      handler() {
        if (this.originalParserConfig == null) {
          this.formIsDirty = false;
        } else {
          this.formIsDirty = !isEqual(this.originalParserConfig, this.deviceTypeParser);
        }
      },
      deep: true
    }
  },
  mounted() {
    if (this.deviceTypeDataInternal && !this.deviceTypeDataInternal.parser) {
      this.deviceTypeDataInternal.parser = {
        config: {}
      };
    }
  },
  methods: {
    beforeRouteLeave(to, from, next) {
      // If the form is dirty and the user did not confirm leave,
      // prevent losing unsaved changes by canceling navigation
      if (this.formIsDirty) {
        this.unsavePromptNextPersist = next;
        this.parserSavePrompt = true;
      } else {
        next();
      }
    },
    beforeWindowUnload(e) {
      if (this.formIsDirty) {
        // Cancel the event
        e.preventDefault();
        // Chrome requires returnValue to be set
        e.returnValue = '';
        // eslint-disable-next-line no-empty-function
        this.unsavePromptNextPersist = () => {};
        this.parserSavePrompt = true;
      }
    },
    switchParserType() {
      this.switchParserPrompt = false;
      this.deviceTypeDataInternal.parser = {
        type: 'unset',
        config: {}
      };
      this.emitChanges();
    },
    parserConfigUpdated(newConfig) {
      if (newConfig) {
        set(this.deviceTypeDataInternal, 'parser.config', newConfig);
      }
      this.$nextTick(() => {
        this.formComponent = get(this.$refs, 'formComponent.formElement', null);
      });
      if (this.emitInsteadOfSave) {
        this.emitChanges();
      } else {
        Object.assign(this.deviceTypeData, this.deviceTypeDataInternal);
      }
    },
    parserUpdated(newConfig) {
      if (newConfig) {
        set(this.deviceTypeDataInternal, 'parser', newConfig);
      }
      this.parserConfigUpdated();
    },
    saveParserPreflight() {
      const calculations = get(this.deviceTypeDataInternal, 'parser.config.calculateElements', []);
      const lowercase_keys = calculations.map(entry => entry.target.toLowerCase());
      const unique_keys = new Set(lowercase_keys);
      if (lowercase_keys.length !== unique_keys.size) {
        this.parserDuplicateKeysPrompt = true;
        return;
      }
      this.saveParser();
    },
    emitChanges() {
      const clonedData = cloneDeep(this.deviceTypeDataInternal);
      this.$emit('save-parser', clonedData);
    },
    saveParser() {
      if (this.emitInsteadOfSave) {
        this.emitChanges();
        return;
      }
      NiotaClassicApiService.deviceTypesUpdate(this.deviceTypeId, this.deviceTypeDataInternal, 'parser').then(() => {
        this.$snack(this.$i18n_t('NiotaClassic.DeviceTypes.snacks.parserSaved'), 'success');
        this.originalParserConfig = cloneDeep(this.deviceTypeDataInternal.parser);
        this.formIsDirty = false;
      }).catch(() => {
        this.$snack(this.$i18n_t('NiotaClassic.DeviceTypes.snacks.parserSaveFailed'), 'error');
      });
    }
  }
};